import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, Download, LogOut, RefreshCw, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "glh_admin_token";

const PROFESSION_LABELS = {
  educator: "Educator",
  healthcare: "Healthcare Professional",
  "first-responder": "First Responder",
  military: "Military Member",
  government: "Government Employee",
  "non-profit": "Non-Profit Employee",
};

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const detailToString = (d) =>
  typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e?.msg || "").join(" ") : "Something went wrong.";

export const AdminDashboard = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [leads, setLeads] = useState(null);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [unread, setUnread] = useState(0);
  const [lastSeen, setLastSeen] = useState("");

  const load = useCallback(async (t) => {
    setError("");
    try {
      const res = await fetch(`${API}/leads`, { headers: { Authorization: `Bearer ${t}` } });
      if (res.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        return;
      }
      if (!res.ok) throw new Error();
      const data = await res.json();
      setLeads(data.leads);
      setUnread(data.unread_count || 0);
      setLastSeen(data.last_seen_at || "");
    } catch {
      setError("Could not load leads. Check your connection and try again.");
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async () => {
    setError("");
    if (!email.trim() || !password) return setError("Enter your email and password.");
    setBusy(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(detailToString(data.detail));
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setLeads(null);
  };

  const STATUSES = ["new", "contacted", "in-progress", "closed"];
  const STATUS_STYLES = {
    new: "bg-[#FEF3C7] text-[#B45309] border-[#F59E0B]/50",
    contacted: "bg-[#0B162C] text-[#FEF3C7] border-[#0B162C]",
    "in-progress": "bg-[#DC2626]/10 text-[#DC2626] border-[#DC2626]/40",
    closed: "bg-emerald-50 text-emerald-700 border-emerald-300",
  };

  const setStatus = async (id, status) => {
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
    try {
      const res = await fetch(`${API}/leads/${id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error();
    } catch {
      setError("Could not update status. Refresh and try again.");
      load(token);
    }
  };

  const [editingNote, setEditingNote] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [digestBusy, setDigestBusy] = useState(false);
  const [digestMsg, setDigestMsg] = useState("");

  const saveNote = async (id) => {
    const note = noteDraft.trim();
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, note } : l)));
    setEditingNote(null);
    try {
      const res = await fetch(`${API}/leads/${id}/note`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ note }),
      });
      if (!res.ok) throw new Error();
    } catch {
      setError("Could not save note. Refresh and try again.");
      load(token);
    }
  };

  const sendDigest = async () => {
    setDigestBusy(true);
    setDigestMsg("");
    try {
      const res = await fetch(`${API}/digest/send-now`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      setDigestMsg("Digest emailed to CMooreMathis@prmg.net");
    } catch {
      setDigestMsg("Could not send digest — try again.");
    } finally {
      setDigestBusy(false);
    }
  };

  const exportCsv = () => {
    const header = ["Date", "Name", "Email", "Phone", "Goal", "Profession", "Est. Loan", "Status", "Confirmation Code"];
    const rows = leads.map((l) => [
      fmtDate(l.created_at),
      l.name,
      l.email,
      l.phone,
      l.goal,
      PROFESSION_LABELS[l.profession] || l.profession,
      l.loan_range,
      l.status || "new",
      l.confirmation_code,
    ]);
    const csv = [header, ...rows].map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "georgia-local-heroes-leads.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (!token) {
    return (
      <div data-testid="admin-login" className="flex min-h-screen items-center justify-center bg-[#060D1A] px-5">
        <div className="w-full max-w-sm rounded-3xl bg-[#FAF8F5] p-8 shadow-2xl">
          <div className="mb-6 flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#0B162C] text-[#F59E0B]">
              <ShieldCheck size={20} />
            </span>
            <div>
              <h1 className="font-display text-xl font-bold text-[#0B162C]">Lead Dashboard</h1>
              <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                Georgia Local Heroes · Private
              </p>
            </div>
          </div>
          <input
            data-testid="admin-email-input"
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mb-3 w-full rounded-xl border border-[#0B162C]/20 bg-white px-5 py-3.5 text-sm outline-none transition-colors focus:border-[#DC2626]"
          />
          <input
            data-testid="admin-password-input"
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && login()}
            className="mb-3 w-full rounded-xl border border-[#0B162C]/20 bg-white px-5 py-3.5 text-sm outline-none transition-colors focus:border-[#DC2626]"
          />
          {error && (
            <p data-testid="admin-login-error" className="mb-3 text-xs font-semibold text-[#DC2626]">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-btn"
            onClick={login}
            disabled={busy}
            className="w-full rounded-full bg-[#DC2626] py-3.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#B91C1C] disabled:opacity-60"
          >
            {busy ? "Signing In…" : "Sign In"}
          </button>
          <a
            href="/"
            data-testid="admin-back-link"
            className="mt-5 flex items-center justify-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 hover:text-[#0B162C]"
          >
            <ArrowLeft size={12} /> Back to site
          </a>
        </div>
      </div>
    );
  }

  const buying = leads?.filter((l) => l.goal === "buying").length ?? 0;
  const refinancing = leads?.filter((l) => l.goal === "refinancing").length ?? 0;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#FAF8F5]">
      <header className="bg-[#060D1A] px-5 py-4 sm:px-8">
        <div className="mx-auto flex max-w-7xl items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/assets/prmg-logo.png" alt="PRMG" className="h-7 w-auto" />
            <span className="hidden h-6 w-px bg-[#F59E0B]/60 sm:block" />
            <span className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-[#FEF3C7]">
              Lead Dashboard
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-btn"
              onClick={() => load(token)}
              className="flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-[#FAF8F5]/80 transition-colors hover:border-[#F59E0B] hover:text-[#F59E0B]"
            >
              <RefreshCw size={13} /> Refresh
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="flex items-center gap-2 rounded-full bg-[#DC2626] px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-white transition-colors hover:bg-[#B91C1C]"
            >
              <LogOut size={13} /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <h1 className="font-display text-2xl font-bold text-[#0B162C] sm:text-3xl">Captured Leads</h1>
            <p className="mt-1 text-sm text-slate-500">Every qualification request, newest first.</p>
            {unread > 0 && (
              <span
                data-testid="unread-badge"
                className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#F59E0B] px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-[#060D1A] shadow-[0_6px_20px_rgba(245,158,11,0.45)]"
              >
                <span className="h-2 w-2 animate-pulse rounded-full bg-[#060D1A]" />
                {unread} new lead{unread === 1 ? "" : "s"} since your last visit
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-3" data-testid="admin-stats">
            {[
              { label: "Total Leads", value: leads?.length ?? "—", testId: "stat-total" },
              { label: "Buying", value: leads ? buying : "—", testId: "stat-buying" },
              { label: "Refinancing", value: leads ? refinancing : "—", testId: "stat-refinancing" },
              { label: "New Since Last Visit", value: leads ? unread : "—", testId: "stat-new" },
            ].map((s) => (
              <div
                key={s.label}
                data-testid={s.testId}
                className={`rounded-2xl px-6 py-4 ${
                  s.testId === "stat-new" && unread > 0 ? "bg-[#DC2626] shadow-[0_8px_24px_rgba(220,38,38,0.35)]" : "bg-[#0B162C]"
                }`}
              >
                <p className="font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-[#F59E0B]">{s.label}</p>
                <p className="mt-1 font-display text-2xl font-black text-[#FAF8F5]">{s.value}</p>
              </div>
            ))}
          </div>
        </div>

        {error && (
          <p data-testid="admin-load-error" className="mt-6 text-sm font-semibold text-[#DC2626]">
            {error}
          </p>
        )}

        <div className="mt-8 overflow-hidden rounded-2xl border border-[#E2E8F0] bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#E2E8F0] px-5 py-4">
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
              {leads ? `${leads.length} record${leads.length === 1 ? "" : "s"}` : "Loading…"}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              {digestMsg && (
                <span data-testid="digest-msg" className="text-xs font-semibold text-slate-500">
                  {digestMsg}
                </span>
              )}
              <button
                data-testid="digest-send-btn"
                onClick={sendDigest}
                disabled={digestBusy}
                className="rounded-full border border-[#0B162C]/20 px-5 py-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-[#0B162C] transition-colors hover:border-[#0B162C] disabled:opacity-40"
              >
                {digestBusy ? "Sending…" : "Email Digest Now"}
              </button>
              <button
                data-testid="export-csv-btn"
                onClick={exportCsv}
                disabled={!leads || leads.length === 0}
                className="flex items-center gap-2 rounded-full bg-[#0B162C] px-5 py-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-[#FAF8F5] transition-colors hover:bg-[#132342] disabled:opacity-40"
              >
                <Download size={13} /> Export CSV
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table data-testid="leads-table" className="w-full min-w-[1080px] text-left text-sm">
              <thead>
                <tr className="border-b border-[#E2E8F0] font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">
                  {["Date", "Name", "Contact", "Goal", "Profession", "Est. Loan", "Status", "Note", "Code"].map((h) => (
                    <th key={h} className="px-5 py-3 font-bold">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {leads && leads.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-5 py-12 text-center text-slate-400" data-testid="leads-empty">
                      No leads yet — they'll appear here the moment a hero qualifies.
                    </td>
                  </tr>
                )}
                {leads?.map((l, i) => (
                  <tr key={l.id} data-testid={`lead-row-${i}`} className="border-b border-[#F1F5F9] transition-colors hover:bg-[#FAF8F5]">
                    <td className="whitespace-nowrap px-5 py-4 text-slate-500">{fmtDate(l.created_at)}</td>
                    <td className="px-5 py-4 font-semibold text-[#0B162C]">
                      <span className="flex items-center gap-2">
                        {lastSeen && l.created_at > lastSeen && (
                          <span
                            data-testid={`lead-new-dot-${i}`}
                            title="New since your last visit"
                            className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-[#F59E0B]"
                          />
                        )}
                        {l.name}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <a href={`mailto:${l.email}`} className="block text-[#DC2626] hover:underline">
                        {l.email}
                      </a>
                      <a href={`tel:${l.phone}`} className="block text-slate-500 hover:underline">
                        {l.phone}
                      </a>
                    </td>
                    <td className="px-5 py-4 capitalize">{l.goal}</td>
                    <td className="px-5 py-4">{PROFESSION_LABELS[l.profession] || l.profession}</td>
                    <td className="px-5 py-4">{l.loan_range}</td>
                    <td className="px-5 py-4">
                      <select
                        data-testid={`lead-status-${i}`}
                        value={l.status || "new"}
                        onChange={(e) => setStatus(l.id, e.target.value)}
                        className={`cursor-pointer appearance-none rounded-full border px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.12em] outline-none transition-colors ${
                          STATUS_STYLES[l.status || "new"]
                        }`}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="max-w-[220px] px-5 py-4">
                      {editingNote === l.id ? (
                        <div className="flex flex-col gap-2">
                          <textarea
                            data-testid={`lead-note-input-${i}`}
                            value={noteDraft}
                            onChange={(e) => setNoteDraft(e.target.value)}
                            rows={2}
                            maxLength={500}
                            placeholder="e.g. Left voicemail Tuesday"
                            autoFocus
                            className="w-full rounded-lg border border-[#0B162C]/20 px-3 py-2 text-xs outline-none focus:border-[#DC2626]"
                          />
                          <div className="flex gap-2">
                            <button
                              data-testid={`lead-note-save-${i}`}
                              onClick={() => saveNote(l.id)}
                              className="rounded-full bg-[#0B162C] px-3 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.15em] text-white"
                            >
                              Save
                            </button>
                            <button
                              data-testid={`lead-note-cancel-${i}`}
                              onClick={() => setEditingNote(null)}
                              className="rounded-full border border-[#0B162C]/20 px-3 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.15em] text-slate-500"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          data-testid={`lead-note-edit-${i}`}
                          onClick={() => {
                            setEditingNote(l.id);
                            setNoteDraft(l.note || "");
                          }}
                          className="text-left text-xs transition-colors hover:text-[#0B162C]"
                        >
                          {l.note ? (
                            <span data-testid={`lead-note-${i}`} className="line-clamp-2 text-slate-600">
                              {l.note}
                            </span>
                          ) : (
                            <span data-testid={`lead-note-${i}`} className="font-mono text-[10px] font-bold uppercase tracking-[0.12em] text-[#D97706]">
                              + Add note
                            </span>
                          )}
                        </button>
                      )}
                    </td>
                    <td className="px-5 py-4 font-mono text-xs font-bold text-[#D97706]">{l.confirmation_code}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};
